import { v4 as uuidv4 } from 'uuid';
import { routesConfig } from '~/components/sidebar/routesConfig';
import Constants from '~/utils/constants';

// Auditoria de acesso: cada abertura de tela pelo menu e uma "visita". Todas as
// chamadas feitas dentro dela levam o mesmo correlation-id e a rota da tela de
// menu (correlation-route), para o ms-access-control contar 1 acesso por visita
// em vez de 1 por endpoint.

export const AUDIT_HEADER_CORRELATION_ID = 'correlation-id';
export const AUDIT_HEADER_CORRELATION_ROUTE = 'correlation-route';

const STORAGE_KEY = '@NovaEra:auditoriaVisita';

type MenuItem = {
  label?: string;
  route?: string;
  roles?: string[];
  subMenu?: MenuItem[];
};

// section: label do item pai no menu (ex.: "Mesa de Crédito"), sem valor
// para itens do primeiro nível.
export type AuditScreen = { route: string; label: string; section?: string };

type AuditVisit = { id: string; route: string; sessao: string | null };

// Itens com roles vazias sao entradas so de breadcrumb (detalhes) e nao aparecem
// no menu; a tela de auditoria agrupa por item clicavel do menu.
const collectScreens = (
  items: MenuItem[],
  acc: AuditScreen[],
  section?: string,
) => {
  items.forEach((item) => {
    if (
      item.route &&
      item.roles?.length &&
      !acc.some((screen) => screen.route === item.route)
    ) {
      acc.push({ route: item.route, label: item.label ?? item.route, section });
    }
    if (item.subMenu) collectScreens(item.subMenu, acc, item.label ?? section);
  });
  return acc;
};

// Nome da tela com a seção do menu, como no breadcrumb: "Mesa de Crédito ›
// Relatório". Sem repetir quando seção e item têm o mesmo nome ("Vendas").
export const formatScreenLabel = ({ label, section }: AuditScreen): string =>
  section && section !== label ? `${section} › ${label}` : label;

let screensCache: AuditScreen[] | null = null;

export const getAuditScreens = (): AuditScreen[] => {
  if (!screensCache) {
    screensCache = collectScreens(routesConfig as MenuItem[], []);
  }
  return screensCache;
};

const normalizePath = (pathname: string) => {
  const path = (pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return path || '/';
};

// Mesma regra do back (ms-relatorio, sanitizarTela): 6+ digitos viram :id, para
// nao levar CPF ou numero de cartao no header.
const sanitizePath = (path: string) =>
  path.replace(/\d(?:[\d.\-/]*\d){5,}/g, ':id');

// Fluxos que seguem em rotas fora do prefixo da tela de menu que os abriu.
// Chave = prefixo da rota do fluxo, valor = rota do item de menu.
export const AUDIT_FLOW_ROUTES: Record<string, string> = {
  '/criar-campanha': '/campanha',
  '/lista-cliente-campanha': '/campanha',
  '/communication': '/campanha',
  '/communication-preview': '/campanha',
  '/define-communication-actions': '/campanha',
  '/sales/indicationLinkCreate': '/sales/indicationLink',
  '/sales/indicationLinkEdit': '/sales/indicationLink',
  '/invoice/advance-financing': '/invoice/antecipation',
  '/file/documents': '/file/documents/history',
  '/parameters/events-center': '/parameters/all',
};

const matches = (path: string, prefix: string) =>
  prefix !== '/' && (path === prefix || path.startsWith(`${prefix}/`));

// Tela de menu dona do pathname: o prefixo mais longo entre os itens do menu e
// os fluxos mapeados (ex.: /consult/user/detail -> /consult/user). Sem dono, a
// propria rota.
export const resolveAuditRoute = (pathname: string): string => {
  const path = normalizePath(pathname);
  const owner = [
    ...getAuditScreens().map(({ route }) => ({ prefix: route, route })),
    ...Object.entries(AUDIT_FLOW_ROUTES).map(([prefix, route]) => ({
      prefix,
      route,
    })),
  ]
    .filter(({ prefix }) => matches(path, prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return owner ? owner.route : sanitizePath(path);
};

const currentSession = () => localStorage.getItem(Constants.SESSION_ID);

const readVisit = (): AuditVisit | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuditVisit) : null;
  } catch {
    return null;
  }
};

const writeVisit = (visit: AuditVisit) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visit));
  } catch {
    // sem storage (modo privado/bloqueado): o id vale so ate o proximo request
  }
  return visit;
};

// Clique em item do menu: sempre uma visita nova, mesmo reabrindo a mesma tela.
export const startAuditVisit = (route: string): AuditVisit =>
  writeVisit({
    id: uuidv4(),
    route: resolveAuditRoute(route),
    sessao: currentSession(),
  });

// Troca de tela por qualquer caminho (menu, link interno, voltar do navegador,
// URL direta) ou novo login abre visita nova; dentro da mesma tela mantem o id.
export const syncAuditVisit = (pathname: string): AuditVisit => {
  const route = resolveAuditRoute(pathname);
  const visit = readVisit();
  if (visit && visit.route === route && visit.sessao === currentSession()) {
    return visit;
  }
  return startAuditVisit(route);
};

export const getAuditHeaders = (
  pathname: string = window.location.pathname,
): Record<string, string> => {
  const visit = syncAuditVisit(pathname);
  return {
    [AUDIT_HEADER_CORRELATION_ID]: visit.id,
    [AUDIT_HEADER_CORRELATION_ROUTE]: visit.route,
  };
};
